// src/components/personal/sections/OrdersSection/hooks/useOrdersInfo.ts
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Order, OrderStatus, Payment } from '../types/types';
import { displayErrorNotification } from '../error/error-handlers';
// Импортируем функцию форматирования из dateUtils
import { formatDateTime } from '@/utils/dateUtils';

interface UseOrderInfoResult {
  orderId: string | null;
  orderInfo: Order | null;
  isLoading: boolean;
  error: string | null;
  setOrderId: (id: string | null) => void;
  refreshOrderInfo: () => Promise<void>;
  updateOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
  addPayment: (orderId: string, paymentData: Partial<Payment>) => Promise<void>;
}

// Используем функцию из dateUtils, но создаем обертку для совместимости 
// с существующим кодом, который ожидает функцию с именем formatDate
export const formatDate = formatDateTime;

export const useOrdersInfo = (): UseOrderInfoResult => {
  const { t } = useTranslation(['orders', 'common', 'errors']);
  const { businessId } = useBusinessId();
  
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderInfo, setOrderInfo] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Загрузка полной информации о заказе
   */
  const fetchOrderInfo = useCallback(async (id: string) => {
    if (!id || !businessId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Используем новый API-эндпоинт для получения полной информации о заказе
      const response = await api.get(`/api/orders/${id}/full-info/?business=${businessId}`);
      
      // Обеспечиваем инициализацию всех необходимых полей
      const safeResponse = {
        ...response,
        order_data: response.order_data || [],
        payments: response.payments || [],
        cost_data: response.cost_data || {},
        user_data: response.user_data || {}
      };
      
      // Извлекаем информацию о доставке из user_data
      let deliveryAddress = null;
      let deliveryComment = null;
      
      if (safeResponse.user_data) {
        // Формируем адрес доставки из компонентов
        const addressParts = [];
        if (safeResponse.user_data.street) addressParts.push(safeResponse.user_data.street);
        if (safeResponse.user_data.house) addressParts.push(`дом ${safeResponse.user_data.house}`);
        if (safeResponse.user_data.apartment) addressParts.push(`кв. ${safeResponse.user_data.apartment}`);
        
        if (addressParts.length > 0) {
          deliveryAddress = addressParts.join(', ');
        }
        
        // Получаем комментарий
        deliveryComment = safeResponse.user_data.comment || null;
      }
      
      // Обогащаем данные заказа дополнительной информацией
      const enrichedOrder = {
        ...safeResponse,
        deliveryAddress,
        deliveryComment
      };
      
      // Сохраняем обогащенные данные заказа
      setOrderInfo(enrichedOrder);
      return enrichedOrder;
    } catch (err: any) {
      console.error('Error fetching order info:', err);
      
      displayErrorNotification({
        message: err.message || 'Не удалось загрузить информацию о заказе',
        code: 'ORDER_INFO_FETCH_ERROR'
      });

      setError(err.message || 'Не удалось загрузить информацию о заказе');
      setOrderInfo(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  /**
   * Обновление данных при изменении ID заказа
   */
  useEffect(() => {
    // Если ID не задан, очищаем данные
    if (!orderId) {
      setOrderInfo(null);
      return;
    }
    
    // Загружаем данные заказа
    fetchOrderInfo(orderId);
  }, [orderId, fetchOrderInfo]);

  /**
   * Обновление статуса заказа
   */
  const updateOrderStatus = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    if (!businessId) {
      setError('ID бизнеса не доступен');
      return;
    }

    setIsLoading(true);
    try {
      // Отправляем запрос на обновление статуса
      await api.patch(`/api/orders/${orderId}/`, { 
        status: newStatus,
        business: businessId
      });
      
      // Обновляем локальное состояние
      if (orderInfo && orderInfo.id === orderId) {
        setOrderInfo({
          ...orderInfo,
          status: newStatus
        });
      }
    } catch (error: any) {
      console.error(t('errors.status_update_error'), error);
      setError(error.message || 'Ошибка обновления статуса');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [t, businessId, orderInfo]);

  /**
   * Добавление платежа к заказу
   */
  const addPayment = useCallback(async (orderId: string, paymentData: Partial<Payment>) => {
    if (!businessId) {
      setError('ID бизнеса не доступен');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(`/api/orders/${orderId}/add-payment/`, {
        ...paymentData,
        business: businessId
      });
      
      // Обновляем платежи в локальном состоянии
      if (orderInfo && orderInfo.id === orderId) {
        const payments = orderInfo.payments || [];
        setOrderInfo({
          ...orderInfo,
          payments: [...payments, response]
        });
      }
    } catch (error: any) {
      console.error(t('errors.payment_add_error'), error);
      setError(error.message || 'Ошибка добавления платежа');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [t, businessId, orderInfo]);

  /**
   * Обновление данных заказа
   */
  const refreshOrderInfo = useCallback(async () => {
    if (orderId) {
      await fetchOrderInfo(orderId);
    }
  }, [orderId, fetchOrderInfo]);

  return {
    orderId,
    orderInfo,
    isLoading,
    error,
    setOrderId,
    refreshOrderInfo,
    updateOrderStatus,
    addPayment
  };
};

export default useOrdersInfo;